<template>
  <div>
    <div>
      <h3 class="text-center">{{ yearMonthLabel }}</h3>
    </div>
    <div class="calendar">
      <div class="calendar__row">
        <div
          v-for="key in Object.keys(headers)"
          :key="key"
          :class="[key, 'calendar__cell header']"
          style="background-color: #f0f0f0;"
        >
          {{ headers[key] }}
        </div>
      </div>
      <div 
        class="calendar__row"
        v-for="(cells, i) in dateCells"
        :key="i"
      >
        <div
          class="calendar__cell body"
          v-for="cell in cells"
          :key="cell.id"
        >
          <template v-if="cell.shouldShow">
            {{ cell.date }}
            <slot name="date" :date="cell.date"></slot>
            <v-btn
              icon
              variant="text"
              :color="cell.isSelected ? 'primary' : 'grey'"
              @click="selectDate(cell.date)"
            >
              <v-icon v-if="cell.isSelected">mdi-checkbox-marked</v-icon>
              <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
            </v-btn>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import dayjs from 'dayjs';
import { defineComponent, PropType } from 'vue';

interface DataType {
  headers: Record<string, string>;
}

type DateCellMeta = {
  id: string; // 追加: Vueのv-forエラー回避用のユニークID
  label: string;
  year: number;
  month: number;
  date: number;
  day: number;
  dayString: string;
  shouldShow: boolean;
  isSelected: boolean;
}

export default defineComponent({
  emits: ['update:modelValue'],

  props: {
    yearMonth: {
      type: String
    },
    modelValue: {
      type: Array as PropType<number[]>,
      default: () => []
    }
  },

  data: (): DataType => ({
    headers: {
      monday: '月',
      tuesday: '火',
      wednesday: '水',
      thursday: '木',
      friday: '金',
      saturday: '土',
      sunday: '日'
    }
  }),

  computed: {
    dateCells(): DateCellMeta[][] {
      if (!this.yearMonth) return [];

      const startOfMonth = dayjs(this.yearMonth).startOf('month');
      const endOfMonth = startOfMonth.endOf('month');

      // カレンダーの左上（開始日）を計算（月曜始まり）
      let currentDay = startOfMonth;
      if (currentDay.day() === 0) {
        // 1日が日曜日の場合、6日戻って前月の月曜日にする
        currentDay = currentDay.subtract(6, 'day');
      } else {
        // それ以外の場合、その週の月曜日に戻る
        currentDay = currentDay.subtract(currentDay.day() - 1, 'day');
      }

      const dateCells: DateCellMeta[][] = [];

      // endOfMonth（月末）を過ぎるまで週（行）を作り続ける
      while (currentDay.isBefore(endOfMonth) || currentDay.isSame(endOfMonth, 'day')) {
        const week: DateCellMeta[] = [];
        
        for (let d = 0; d < 7; d++) {
          const isSelected = this.modelValue.includes(currentDay.date());
          
          week.push({
            id: currentDay.format('YYYY-MM-DD'), // 例: "2023-11-01"
            label: currentDay.date() === 1 ? currentDay.format('M/D') : currentDay.format('D'),
            year: currentDay.year(),
            month: currentDay.month(),
            date: currentDay.date(),
            day: currentDay.day(),
            dayString: '',
            shouldShow: currentDay.month() === startOfMonth.month(),
            isSelected,
          });
          
          currentDay = currentDay.add(1, 'day'); // 1日進める
        }
        dateCells.push(week);
      }
      
      return dateCells;
    },
    yearMonthLabel() {
      return this.yearMonth ? dayjs(this.yearMonth).format('YYYY年MM月') : '';
    },
  },

  methods: {
    selectDate(date: number) {
      if (this.modelValue.includes(date)) {
        const newValue = [...this.modelValue].filter((v) => v !== date);
        this.$emit('update:modelValue', newValue);
      } else {
        const newValue = [...this.modelValue];
        newValue.push(date);
        this.$emit('update:modelValue', newValue);
      }
    }
  }
});
</script>

<style lang="scss">
.calendar__row {
  display: flex;
  border-bottom: solid 1px #e0e0e0;
  border-left: solid 1px #e0e0e0;
}
.calendar__cell {
  flex: 1;
  border-right: solid 1px #e0e0e0;
  
  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 8px;
}
</style>